import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, XCircle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// Coach keep/cancel decision for bookings flagged after a player's assessment was cancelled or moved.
export function AtRiskBookingsPanel({ studentId, studentName }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/coach/players/${studentId}/at-risk-bookings`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (items?.length && window.location.hash === "#at-risk") document.getElementById("at-risk")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [items]);
  if (!items?.length) return null;
  const act = async (b, kind) => {
    setBusy(b.booking_id + kind);
    try {
      if (kind === "keep") await api.post(`/bookings/${b.booking_id}/at-risk/keep`);
      else await api.post(`/bookings/${b.booking_id}/cancel-any`, { reason: "Assessment not completed", coach_override: true });
      toast.success(kind === "keep" ? "Booking kept" : "Booking cancelled — player notified");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };
  return (
    <div id="at-risk" className="card-surface p-5 border-l-4 border-[#FF9F1C] space-y-3 scroll-mt-24" data-testid="at-risk-panel">
      <div className="flex items-center gap-2 text-[#FF9F1C] font-bold text-sm">
        <AlertTriangle className="w-4 h-4 shrink-0" /> <span data-testid="at-risk-banner-text">This player has not yet completed their assessment. The following sessions may need to be reviewed before they can proceed.</span>
      </div>
      {items.map((b) => (
        <div key={b.booking_id} className="flex flex-wrap items-center justify-between gap-3 py-2 border-t border-[#262626]" data-testid={`at-risk-row-${b.booking_id}`}>
          <div className="text-sm">
            {b.offering_id ? <Link to={`/offerings/${b.offering_id}`} className="font-semibold hover:text-[#CCFF00]">{b.title}</Link> : <span className="font-semibold">{b.title}</span>}
            <div className="text-xs text-[#A3A3A3]">{b.when}</div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => act(b, "keep")} disabled={!!busy} data-testid={`at-risk-keep-${b.booking_id}`}
              className="min-h-[36px] px-3 py-1.5 text-[11px] uppercase tracking-wider border border-[#404040] hover:border-[#22C55E] hover:text-[#22C55E] inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy === b.booking_id + "keep" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Keep
            </button>
            <button type="button" onClick={() => act(b, "cancel")} disabled={!!busy} data-testid={`at-risk-cancel-${b.booking_id}`}
              className="min-h-[36px] px-3 py-1.5 text-[11px] uppercase tracking-wider border border-[#404040] hover:border-[#FF4500] hover:text-[#FF4500] inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy === b.booking_id + "cancel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Cancel
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
